import { useEffect, useState } from 'react';

import { apiGet } from '../lib/api';

interface UsageSummaryResponse {
  from: string;
  to: string;
  totals: {
    raw_cost_usd: number;
    billable_amount_usd: number;
    requests_count: number;
    input_tokens: number;
    output_tokens: number;
  };
  by_provider: Array<{
    provider: string;
    total_billable: number;
    total_raw: number;
    total_requests: number;
  }>;
  by_model: Array<{
    provider: string;
    model: string;
    total_billable: number;
    total_raw: number;
    total_requests: number;
    total_input: number;
    total_output: number;
  }>;
  by_project: Array<{
    project_id: string;
    project_name: string;
    total_billable: number;
    total_raw: number;
    total_requests: number;
    total_input: number;
    total_output: number;
  }>;
  openai_monthly_by_project: Array<{
    month: string;
    project_id: string;
    project_name: string;
    total_raw_usd: number;
    total_billable_usd: number;
    total_requests: number;
  }>;
  anomalies: Array<{
    project_id: string;
    project_name: string;
    day: string;
    day_billable: number;
    baseline: number;
  }>;
}

interface OpenAiLiveUsageResponse {
  status: 'ok' | 'unconfigured' | 'error';
  from: string;
  to: string;
  error?: string;
  totals: {
    input_tokens: number;
    output_tokens: number;
    requests_count: number;
  };
  by_openai_project: Array<{
    openai_project_id: string;
    total_input: number;
    total_output: number;
    total_requests: number;
    models_count: number;
    api_keys_count: number;
  }>;
  by_openai_api_key: Array<{
    openai_api_key_id: string;
    total_input: number;
    total_output: number;
    total_requests: number;
  }>;
  by_model: Array<{
    model: string;
    total_input: number;
    total_output: number;
    total_requests: number;
  }>;
}

export function UsagePage() {
  const [data, setData] = useState<UsageSummaryResponse | null>(null);
  const [openAiLive, setOpenAiLive] = useState<OpenAiLiveUsageResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      try {
        const [summaryResult, openAiResult] = await Promise.allSettled([
          apiGet<UsageSummaryResponse>('/api/usage/summary'),
          apiGet<OpenAiLiveUsageResponse>('/api/usage/openai-live'),
        ]);

        if (summaryResult.status === 'fulfilled') {
          setData(summaryResult.value);
        } else {
          setError(
            summaryResult.reason instanceof Error
              ? summaryResult.reason.message
              : 'Failed to load usage summary',
          );
        }

        if (openAiResult.status === 'fulfilled') {
          setOpenAiLive(openAiResult.value);
        } else {
          setOpenAiLive({
            status: 'error',
            from: '',
            to: '',
            error:
              openAiResult.reason instanceof Error
                ? openAiResult.reason.message
                : 'Failed to load OpenAI live usage',
            totals: {
              input_tokens: 0,
              output_tokens: 0,
              requests_count: 0,
            },
            by_openai_project: [],
            by_openai_api_key: [],
            by_model: [],
          });
        }
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  return (
    <div>
      <h1 className="text-xl font-semibold">Usage</h1>
      <p className="mt-1 text-sm text-slate-600">Usage metering by provider, model, and project.</p>

      {isLoading ? (
        <div className="mt-3">
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
            <div className="h-full w-2/3 animate-pulse rounded-full bg-brand-600" />
          </div>
          <p className="mt-2 text-xs text-slate-500">Loading usage data...</p>
        </div>
      ) : null}

      {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}

      {data ? (
        <>
          <div className="mt-4 grid gap-3 md:grid-cols-4">
            <Metric title="Raw cost" value={formatMoney(data.totals.raw_cost_usd)} />
            <Metric title="Billable" value={formatMoney(data.totals.billable_amount_usd)} />
            <Metric title="Requests" value={data.totals.requests_count.toLocaleString()} />
            <Metric
              title="Tokens"
              value={`${data.totals.input_tokens.toLocaleString()} in / ${data.totals.output_tokens.toLocaleString()} out`}
            />
          </div>

          <SectionTable
            title="By provider"
            headers={['Provider', 'Billable', 'Raw', 'Requests']}
            rows={data.by_provider.map((row) => [
              row.provider,
              formatMoney(row.total_billable),
              formatMoney(row.total_raw),
              row.total_requests.toLocaleString(),
            ])}
          />

          <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-lg font-semibold">OpenAI live usage (from OpenAI)</h2>
              <span
                className={`rounded-full px-2 py-1 text-xs font-medium ${openAiLive?.status === 'ok' ? 'bg-emerald-100 text-emerald-800' : openAiLive?.status === 'error' ? 'bg-rose-100 text-rose-800' : 'bg-amber-100 text-amber-800'}`}
              >
                {openAiLive?.status === 'ok'
                  ? 'Connected'
                  : openAiLive?.status === 'error'
                    ? 'Error'
                    : 'Unconfigured'}
              </span>
            </div>
            <p className="mt-1 text-sm text-slate-600">
              Live data grouped by OpenAI project/API key, independent from internal project
              mappings.
            </p>
            <p className="mt-1 text-xs text-slate-500">
              To include this usage in internal billing rollups, configure mappings in Settings →
              API Key Mappings using OpenAI <code>api_key_id</code> or <code>project_id</code>.
            </p>
            {openAiLive?.error ? (
              <p className="mt-2 text-sm text-amber-700">{openAiLive.error}</p>
            ) : null}

            {openAiLive ? (
              <div className="mt-3 grid gap-3 md:grid-cols-3">
                <Metric
                  title="OpenAI requests"
                  value={openAiLive.totals.requests_count.toLocaleString()}
                />
                <Metric
                  title="OpenAI input"
                  value={openAiLive.totals.input_tokens.toLocaleString()}
                />
                <Metric
                  title="OpenAI output"
                  value={openAiLive.totals.output_tokens.toLocaleString()}
                />
              </div>
            ) : null}
          </div>

          <SectionTable
            title="OpenAI by project"
            headers={['OpenAI project', 'Requests', 'Tokens (in/out)', 'Models', 'API keys']}
            rows={(openAiLive?.by_openai_project ?? []).map((row) => [
              row.openai_project_id,
              row.total_requests.toLocaleString(),
              `${row.total_input.toLocaleString()} / ${row.total_output.toLocaleString()}`,
              row.models_count.toLocaleString(),
              row.api_keys_count.toLocaleString(),
            ])}
          />

          <SectionTable
            title="By model"
            headers={['Provider', 'Model', 'Billable', 'Requests', 'Tokens']}
            rows={data.by_model.map((row) => [
              row.provider,
              row.model,
              formatMoney(row.total_billable),
              row.total_requests.toLocaleString(),
              `${row.total_input.toLocaleString()} / ${row.total_output.toLocaleString()}`,
            ])}
          />

          <SectionTable
            title="By project"
            headers={['Project', 'Billable', 'Raw', 'Requests']}
            rows={data.by_project.map((row) => [
              row.project_name,
              formatMoney(row.total_billable),
              formatMoney(row.total_raw),
              row.total_requests.toLocaleString(),
            ])}
          />

          <SectionTable
            title="OpenAI monthly project cost (USD)"
            headers={['Month', 'Project', 'Billable USD', 'Raw USD', 'Requests']}
            rows={(data.openai_monthly_by_project ?? []).map((row) => [
              formatYearMonth(row.month),
              row.project_name,
              formatMoney(row.total_billable_usd),
              formatMoney(row.total_raw_usd),
              row.total_requests.toLocaleString(),
            ])}
          />

          <SectionTable
            title="Anomaly flags"
            headers={['Project', 'Date', 'Daily billable', 'Baseline']}
            rows={data.anomalies.map((row) => [
              row.project_name,
              row.day,
              formatMoney(row.day_billable),
              formatMoney(row.baseline),
            ])}
          />
        </>
      ) : null}
    </div>
  );
}

function Metric(props: { title: string; value: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
      <p className="text-sm text-slate-600">{props.title}</p>
      <p className="mt-2 text-lg font-semibold text-slate-900">{props.value}</p>
    </div>
  );
}

function SectionTable(props: { title: string; headers: string[]; rows: string[][] }) {
  return (
    <div className="mt-6">
      <h2 className="text-lg font-semibold">{props.title}</h2>
      <div className="mt-2 overflow-x-auto">
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-slate-600">
              {props.headers.map((header) => (
                <th key={header} className="py-2 pr-3">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {props.rows.length === 0 ? (
              <tr>
                <td className="py-3 text-slate-500" colSpan={props.headers.length}>
                  No data
                </td>
              </tr>
            ) : (
              props.rows.map((row, index) => (
                <tr key={`${props.title}-${index}`} className="border-b border-slate-100">
                  {row.map((value, cellIndex) => (
                    <td key={`${index}-${cellIndex}`} className="py-2 pr-3">
                      {value}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function formatMoney(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(value);
}

function formatYearMonth(value: string): string {
  const [year, month] = value.split('-');
  if (!year || !month) {
    return value;
  }

  const yearNumber = Number.parseInt(year, 10);
  if (Number.isNaN(yearNumber)) {
    return value;
  }

  const monthNumber = Number.parseInt(month, 10);
  if (Number.isNaN(monthNumber) || monthNumber < 1 || monthNumber > 12) {
    return value;
  }

  const date = new Date(Date.UTC(yearNumber, monthNumber - 1, 1));
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}
